/**
 * useSettingsQueries — TanStack Query hooks for profile and company settings.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useQueryScope } from '../utils/queryScope';

const ROOT = 'settings';

const sharedQueryDefaults = {
  retry: 0,
};

function useScope() {
  return useQueryScope();
}

export function useCurrentUserProfile({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'current-user'],
    queryFn: async () => {
      const { data } = await api.get('/users/me');
      return data;
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useCompanyProfile({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'company'],
    queryFn: async () => {
      const { data } = await api.get('/company');
      return data;
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useCompaniesList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'companies'],
    queryFn: async () => {
      const { data } = await api.get('/companies');
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

export function useSettingsInvalidator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useMemo(() => {
    const root = [ROOT, scope];
    const inv = (key) => queryClient.invalidateQueries({ queryKey: key });

    return {
      currentUser: () => inv([...root, 'current-user']),
      company: () => inv([...root, 'company']),
      companies: () => inv([...root, 'companies']),
      all: () => inv(root),
    };
  }, [queryClient, scope]);
}

// ============================================================================
// MUTATIONS
// ============================================================================

export function useUpdateCompanyProfile() {
  const invalidate = useSettingsInvalidator();
  return useMutation({
    mutationFn: (formData) => api.put('/company', formData).then((r) => r.data),
    onSuccess: () => invalidate.company(),
  });
}

export function useUploadCompanyLogo() {
  const invalidate = useSettingsInvalidator();
  return useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/company/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data);
    },
    onSuccess: () => invalidate.company(),
  });
}

export function useRemoveCompanyLogo() {
  const invalidate = useSettingsInvalidator();
  return useMutation({
    mutationFn: () => api.delete('/company/logo'),
    onSuccess: () => invalidate.company(),
  });
}

export function useUpdateUserProfile() {
  const invalidate = useSettingsInvalidator();
  return useMutation({
    mutationFn: (formData) => api.put('/users/me', formData).then((r) => r.data),
    onSuccess: () => invalidate.currentUser(),
  });
}

export function useUploadUserPhoto() {
  const invalidate = useSettingsInvalidator();
  return useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/users/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data);
    },
    onSuccess: () => invalidate.currentUser(),
  });
}

export function useChangeUserPassword() {
  return useMutation({
    mutationFn: (passwordData) => api.post('/users/change-password', passwordData),
  });
}
